import { type ComponentPropsWithoutRef } from "react";
import { Filter } from "lucide-react";
import { usePropertyGroups } from "@/components/property-group-select";
import { SearchableSelect } from "@/components/searchable-select";
import { cn } from "@/lib/utils";

interface PropertyGroupFilterProps {
  // Only `propertyGroup` is read, so this accepts a full `Property` as well as
  // the lighter per-feature row shapes some pages fetch (e.g. the collections
  // properties list) without forcing a cast.
  properties: Array<{ propertyGroup: string | null }>;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Extra attributes spread onto the trigger button (title, data-tour, …). */
  triggerProps?: ComponentPropsWithoutRef<"button"> & { [dataAttr: `data-${string}`]: string | undefined };
}

export function PropertyGroupFilter({ properties, value, onChange, className, triggerProps }: PropertyGroupFilterProps) {
  const groupsQuery = usePropertyGroups();
  const propertyGroups = [...new Set([
    ...(groupsQuery.data || []).map(g => g.name),
    ...(properties.map(p => p.propertyGroup).filter(Boolean) as string[]),
  ])].sort();

  if (propertyGroups.length === 0) return null;

  return (
    <SearchableSelect
      value={value}
      onValueChange={onChange}
      options={[
        { value: "all", label: "All Groups", testId: "filter-group-all" },
        ...propertyGroups.map(g => ({ value: g, label: g, testId: `filter-group-${g}` })),
      ]}
      placeholder="All Groups"
      searchPlaceholder="Search groups..."
      emptyText="No groups match."
      testId="select-group-filter"
      className={cn("h-9 w-[180px] text-sm", className)}
      leadingIcon={<Filter className="h-4 w-4 mr-1 shrink-0" />}
      triggerProps={triggerProps}
    />
  );
}
